import React from 'react';
import styled from 'styled-components';
import { apiService } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;
const ContentWrapper = styled.div`
  position: relative; z-index: 3; max-width: 1200px; margin: 0 auto; padding: var(--desktop-page-padding-x, 2rem); padding-top: 6rem;
  @media (max-width: 768px) {
    max-width: none;
    margin: 0;
    padding: 0 var(--mobile-page-padding-x);
    width: 100%;
    padding-top: var(--mobile-page-padding-top);
  }
`;
const SectionCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  margin-bottom: 1rem;
  padding: 1.25rem 1.5rem;
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: .5rem; margin-bottom: .25rem;
  h3{ margin: 0; }
  p{ margin: 0; color: var(--text-light); }
`;
const ExamRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: .75rem; padding: .75rem .5rem; border-bottom: 1px dashed rgba(0,0,0,0.06);
`;

const gradient = (a:string,b:string)=>({ background: `linear-gradient(135deg, ${a}, ${b})` });

const StudentAvailableExams: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string|null>(null);
  const [exams, setExams] = React.useState<any[]>([]);
  const [bookingId, setBookingId] = React.useState<number|null>(null);
  const [nextExam, setNextExam] = React.useState<any|null>(null);
  const navigate = useNavigate();

  const load = React.useCallback(async ()=>{
    setLoading(true); setError(null);
    try {
      const [availResp, nextResp] = await Promise.all([
        apiService.get<any>('/api/exams/student/available'),
        apiService.get<any>('/api/exams/student/next'),
      ]);
      const list = Array.isArray((availResp as any)?.data) ? (availResp as any).data : (Array.isArray(availResp) ? availResp : []);
      const next = (nextResp && typeof nextResp === 'object' && 'data' in nextResp) ? (nextResp as any).data : nextResp;
      setExams(list);
      setNextExam(next || null);
    } catch(e:any){ setError(e?.message || 'Failed to load available exams'); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(()=>{ load(); }, [load]);

  const canBook = React.useCallback((e:any)=>{
    if (!e?.startTime) return false;
    const dt = new Date(e.startTime);
    const future = isFinite(dt.getTime()) ? dt > new Date() : true;
    if (!future) return false;
    if (nextExam?.startTime) {
      try {
        const nextDt = new Date(nextExam.startTime);
        if (isFinite(nextDt.getTime()) && nextDt > new Date()) return false; // already has next upcoming
      } catch {}
    }
    return true;
  }, [nextExam]);

  const format = (iso?:string) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(i18n.language === 'nl' ? 'nl-NL' : 'en-GB',{ day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone:'Europe/Amsterdam' }); }
    catch { return new Date(iso).toLocaleString(i18n.language === 'nl' ? 'nl-NL' : 'en-GB',{ day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  };

  const book = async (id:number) => {
    try {
      setBookingId(id); setError(null);
      await apiService.post<any>(`/api/exams/student/${id}/book`);
      navigate('/portal/student/exams');
    } catch(e:any){ setError(e?.message || 'Failed to book exam'); }
    finally { setBookingId(null); }
  };

  return (
    <PageWrapper>
      <ContentWrapper>
        <SectionCard>
          <Header>
            <div>
              <h3>{t('portal.exams.available_title','Available exams')}</h3>
              <p>{t('portal.exams.available_sub','Choose an exam slot to book')}</p>
            </div>
            <div style={{ display:'flex', gap: '.5rem' }}>
              <button onClick={load} style={{ border:0, borderRadius:10, padding:'.45rem .75rem', fontWeight:800, color:'#fff', cursor:'pointer', ...gradient('#667eea','#764ba2') }}>{t('portal.exams.refresh','Refresh')}</button>
              <button onClick={()=> navigate('/portal/student/exams')} style={{ border:0, borderRadius:10, padding:'.45rem .75rem', fontWeight:800, color:'#fff', cursor:'pointer', ...gradient('#11998e','#38ef7d') }}>{t('portal.exams.my_exams','My exams')}</button>
            </div>
          </Header>
          {loading ? (
            <div style={{ textAlign:'center', padding:'1.25rem', color:'var(--text-light)' }}>{t('portal.common.loading','Loading…')}</div>
          ) : error ? (
            <div style={{ textAlign:'center', padding:'1.25rem', color:'#b00020', fontWeight:700 }}>⚠️ {error}</div>
          ) : exams.length === 0 ? (
            <div style={{ textAlign:'center', padding:'1.25rem', color:'var(--text-light)' }}>{t('portal.exams.none_available','No available exams')}</div>
          ) : (
            <div>
              {exams.map((e:any)=>{
                const allowed = canBook(e);
                const disabled = bookingId === e.id || !allowed;
                return (
                  <ExamRow key={`avail-exam-${e.id}`}>
                    <div style={{ fontWeight: 800 }}>{format(e.startTime)}{e.location ? ` • ${e.location}` : ''}</div>
                    <button
                      onClick={()=> allowed && book(e.id)}
                      disabled={disabled}
                      style={{ opacity: disabled ? .6 : 1, border:0, borderRadius:10, padding:'.35rem .65rem', fontWeight:800, color:'#fff', ...gradient('#11998e','#38ef7d'), cursor: disabled ? 'not-allowed' : 'pointer' }}
                    >{bookingId === e.id ? t('portal.exams.booking','Booking…') : allowed ? t('portal.exams.book','Book') : t('portal.exams.unavailable','Unavailable')}</button>
                  </ExamRow>
                );
              })}
            </div>
          )}
        </SectionCard>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default StudentAvailableExams;
